import { DateRange } from '../constants/ui';

/** Date-only UI ranges include both UTC calendar dates. */
export const parseCustomUtcRange = (start: string, end: string): { startDate: Date; endDate: Date } | null => {
  const parse = (value: string): Date | null => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T00:00:00.000Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : null;
  };
  const startDate = parse(start);
  const endDate = parse(end);
  if (!startDate || !endDate || startDate > endDate) return null;
  endDate.setUTCHours(23, 59, 59, 999);
  return { startDate, endDate };
};

const startOfDayUtc = (date: Date) => {
  const d = new Date(date);
  d.setUTCHours(0, 0, 0, 0);
  return d;
};

const endOfDayUtc = (date: Date) => {
  const d = new Date(date);
  d.setUTCHours(23, 59, 59, 999);
  return d;
};

const shiftDays = (date: Date, days: number) => {
  const shifted = new Date(date);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted;
};

export const getDateRangeFilter = (range: DateRange) => {
  const now = new Date();
  const todayStart = startOfDayUtc(now);
  
  switch (range) {
    case '1hour': {
      // Use current time as end and go back 1 hour
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
      return {
        startDate: oneHourAgo,
        endDate: now,
        label: 'Last Hour'
      };
    }
    case '3hours': {
      // Use current time (not rounded) as end time, and go back 3 hours
      const threeHoursAgo = new Date(now.getTime() - 3 * 60 * 60 * 1000);
      return {
        startDate: threeHoursAgo,
        endDate: now,
        label: 'Last 3 Hours'
      };
    }
    case '6hours': {
      // Use current time as end and go back 6 hours
      const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);
      return {
        startDate: sixHoursAgo,
        endDate: now,
        label: 'Last 6 Hours'
      };
    }
    case '1day':
      return {
        startDate: todayStart,
        // Use "now" to avoid requesting future timestamps for "Today".
        endDate: now,
        label: 'Today'
      };
    case 'yesterday': {
      const yesterday = shiftDays(todayStart, -1);
      return {
        startDate: yesterday,
        endDate: endOfDayUtc(yesterday),
        label: 'Yesterday'
      };
    }
    case '7days': {
      const sevenDaysAgo = shiftDays(todayStart, -6);
      return {
        startDate: sevenDaysAgo,
        endDate: new Date(), // include current time for the latest day
        label: 'Last 7 Days'
      };
    }
    case '30days': {
      const thirtyDaysAgo = shiftDays(todayStart, -29);
      return {
        startDate: thirtyDaysAgo,
        endDate: new Date(),
        label: 'Last 30 Days'
      };
    }
    case '60days': {
      const sixtyDaysAgo = shiftDays(todayStart, -59);
      return {
        startDate: sixtyDaysAgo,
        endDate: new Date(),
        label: 'Last 60 Days'
      };
    }
    case '90days': {
      const ninetyDaysAgo = shiftDays(todayStart, -89);
      return {
        startDate: ninetyDaysAgo,
        endDate: new Date(),
        label: 'Last 90 Days'
      };
    }
    case '120days': {
      const oneHundredTwentyDaysAgo = shiftDays(todayStart, -119);
      return {
        startDate: oneHundredTwentyDaysAgo,
        endDate: new Date(),
        label: 'Last 120 Days'
      };
    }
    case '150days': {
      const oneHundredFiftyDaysAgo = shiftDays(todayStart, -149);
      return {
        startDate: oneHundredFiftyDaysAgo,
        endDate: new Date(),
        label: 'Last 150 Days'
      };
    }
    case '180days': {
      const oneHundredEightyDaysAgo = shiftDays(todayStart, -179);
      return {
        startDate: oneHundredEightyDaysAgo,
        endDate: new Date(),
        label: 'Last 180 Days'
      };
    }
    case '360days': {
      const threeHundredSixtyDaysAgo = shiftDays(todayStart, -359);
      return {
        startDate: threeHundredSixtyDaysAgo,
        endDate: new Date(),
        label: 'Last 360 Days'
      };
    }
    default:
      return {
        startDate: todayStart,
        endDate: now,
        label: 'Today'
      };
  }
};

export const isActive = (lastUsedTimestamp: number | undefined, thresholdDays: number = 14): boolean => {
  if (!lastUsedTimestamp) return false;
  
  const lastUsedDate = new Date(lastUsedTimestamp * 1000);
  const now = new Date();
  const daysDiff = (now.getTime() - lastUsedDate.getTime()) / (1000 * 60 * 60 * 24);
  
  return daysDiff <= thresholdDays;
};

export const formatDate = (timestamp: number): string => {
  return new Date(timestamp * 1000).toLocaleDateString('en-US');
};
